import { useRef, useEffect, useState, useCallback } from 'react';
import { throttle } from 'lodash-es';
import { useTextDirectionality } from '../../theme/text-directionality-provider';

export const useTabsScroll = () => {
  const [isScrolledStart, setIsScrolledStart] = useState(true);
  const [isScrolledEnd, setIsScrolledEnd] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const tabsList = useRef<HTMLDivElement | HTMLUListElement>(null);
  const listWidth = tabsList.current?.offsetWidth;
  const scrollAmount = listWidth ? listWidth / 2 : 0;

  const showScrollBack = !isScrolledStart && hasOverflow;
  const showScrollForward = !isScrolledEnd && hasOverflow;

  const dir = useTextDirectionality();

  const scrollBack = useCallback(() => {
    if (tabsList.current) {
      if (dir === 'ltr') {
        tabsList.current.scrollLeft -= scrollAmount;
      } else {
        tabsList.current.scrollLeft += scrollAmount;
      }
    }
  }, [scrollAmount, dir]);

  const scrollForward = useCallback(() => {
    if (tabsList.current) {
      if (dir === 'ltr') {
        tabsList.current.scrollLeft += scrollAmount;
      } else {
        tabsList.current.scrollLeft -= scrollAmount;
      }
    }
  }, [scrollAmount, dir]);

  // Hide and show Previous and Next buttons only if there is room too scroll
  const showHideControls = () => {
    // hide back button if tabs have been scrolled all the say to the back
    if (tabsList.current && Math.abs(tabsList.current.scrollLeft) < 1) {
      setIsScrolledStart(true);
    } else {
      setIsScrolledStart(false);
    }
    // hide the forward button if tabs have been scrolled all the way to the end
    if (
      tabsList.current &&
      Math.ceil(Math.abs(tabsList.current.scrollLeft)) >=
        tabsList.current.scrollWidth - tabsList.current.offsetWidth
    ) {
      setIsScrolledEnd(true);
    } else {
      setIsScrolledEnd(false);
    }
  };

  // show the scroll buttons only if the list is longer than the scrollable wrapper
  const showControlsOnOverflow = () => {
    const { innerWidth } = window;

    let offsetWidth = 0;
    let scrollWidth = 0;

    if (tabsList.current) {
      offsetWidth = tabsList.current.offsetWidth;
      scrollWidth = tabsList.current.scrollWidth;
    }

    return scrollWidth > offsetWidth || scrollWidth > innerWidth
      ? setHasOverflow(true)
      : setHasOverflow(false);
  };

  useEffect(() => {
    const currentRef = tabsList?.current;

    const onScroll = throttle(() => {
      showHideControls();
    }, 500);

    const onResize = throttle(() => {
      showControlsOnOverflow();
    }, 500);

    currentRef?.addEventListener('scroll', onScroll);

    // check if tablist is overflowing on first load
    showControlsOnOverflow();

    // show/hide scroll controls on resize
    window.addEventListener('resize', onResize);

    return () => {
      currentRef?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      setHasOverflow(false);
      setIsScrolledEnd(false);
      setIsScrolledStart(true);
    };
  }, [tabsList]);

  return {
    scrollBack,
    scrollForward,
    tabsList,
    showScrollBack,
    showScrollForward,
  };
};
